import {
  ChevronLeft,
  ChevronRight,
} from "@lazarentertainment/web-design-system/components/iconOutline"
import { Button } from "client/designSystem/Button"
import { DetailedProductFieldsFragment } from "client/graphql/storefront-api"
import { FormattedMessage } from "react-intl"
import { buttonFocus } from "shared/styles/mixins"
import useArrayElement from "shared/utils/useArrayElement"
import styled from "styled-components"

import { scaleShopifyImageUrl } from "../../utils"

export const ImageContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`

export const SelectImageButton = styled.button.attrs({ type: "button" })`
  appearance: none;
  border: none;
  width: 44%;
  margin: 10px;
  background-color: transparent;

  img {
    border-radius: 2px;
  }

  ${buttonFocus}
`

export const SelectedImageContainer = styled.div`
  width: 100%;
`

export const SelectedImageInnerContainer = styled.div`
  min-height: 60%;
  display: flex;
  justify-content: center;
  align-items: center;
`

export const SelectedImageWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;

  img {
    height: 200px;
    border-radius: 4px;
  }
`

export const NavigationButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 80px;

  gap: 20px;
`

export const ImageNumber = styled.p`
  margin: 0px 20px;
  padding: 0px 5px;
  color: ${(props) => props.theme.text.default};
  text-transform: uppercase;
  font-family: ${(props) => props.theme.font.display};
`

export const ChevronButton = styled.button.attrs({ type: "button" })`
  appearance: none;
  border: none;
  background-color: transparent;
  height: 30px;
  width: 30px;

  &:first-of-type {
    transform: rotate(-90deg);
  }

  &:last-of-type {
    transform: rotate(90deg);
  }

  svg {
    stroke: ${(props) => props.theme.text.default};
    stroke-width: 1px;

    @media (hover: hover) {
      &:hover {
        stroke-width: 2px;
      }
    }
  }

  ${buttonFocus}
`

export const VerticallyCenteredContainer = styled.div`
  width: 100%;
  height: 100%;

  padding: 10px 20px;
  background-color: white;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

export type ImageGalleryProps = {
  product: DetailedProductFieldsFragment
  onBack: () => void
  initialIndex?: number
}

const ImageGallery = ({ product, initialIndex }: ImageGalleryProps) => {
  const [selectedImage, { index, setIndex, next, prev }] = useArrayElement(
    product.images.nodes,
    initialIndex,
  )

  return (
    <VerticallyCenteredContainer>
      {selectedImage ? (
        <SelectedImageContainer>
          <SelectedImageInnerContainer>
            <SelectedImageWrapper>
              <img
                alt={product.title}
                src={scaleShopifyImageUrl(selectedImage.url, {
                  width: 450,
                  height: 500,
                })}
              />
            </SelectedImageWrapper>
          </SelectedImageInnerContainer>
        </SelectedImageContainer>
      ) : (
        <ImageContainer>
          {product.images.nodes.map((image, thisIndex) => (
            <SelectImageButton
              key={image.id}
              onClick={() => {
                setIndex(thisIndex)
              }}
              type="button"
            >
              <img
                alt={product.title}
                src={scaleShopifyImageUrl(image.url)}
              />
            </SelectImageButton>
          ))}
        </ImageContainer>
      )}
      <NavigationButtonContainer>
        <Button
          variant="tertiary-gray"
          icon={ChevronLeft}
          iconOnly
          onClick={prev}
          data-robot="image-gallery-previous"
        />
        <p className="body-md text-color-secondary">
          <FormattedMessage
            id="image.number"
            defaultMessage="Image {ofnumber} of {oftotal}"
            values={{
              ofnumber: index + 1,
              oftotal: product.images.nodes.length,
            }}
          />
        </p>
        <Button
          variant="tertiary-gray"
          icon={ChevronRight}
          iconOnly
          onClick={next}
          data-robot="image-gallery-next"
        />
      </NavigationButtonContainer>
    </VerticallyCenteredContainer>
  )
}

export default ImageGallery
